/* eslint-disable react/jsx-no-useless-fragment */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import type { VideoJsPlayer } from 'video.js';

import { colorPickerColorScheme } from '@helpers/ColorPickerColorScheme';
import { shapesScheme } from '@helpers/ShapesScheme';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useAuth } from '@hooks/useAuth';
import { useResponsive } from '@hooks/useResponsive';
import { LgWidth } from '@components/Responsive';
import { toggleSingInExternalReviewerModal } from '@redux/actions/modalAction';
import useCanvasHistory from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useCanvasHistory';
import {
  withKeysCanvasHistoryChange,
  withKeysCanvasHistoryCopy
} from '@helpers/withKeysCanvasHistoryChange';
// import TextHighlightModal from '@pages/MediaViewer/AssetViewer/PdfCard/components/TextHighlightModal';
// import useOnboardingProcess from '@hooks/useOnboardingProcess';

import { ReactComponent as BackSvg } from '@assets/icons/ShapesDrawingTools/back.svg';
import { ReactComponent as ForwardSvg } from '@assets/icons/ShapesDrawingTools/forward.svg';
import { ReactComponent as ArrowSvg } from '@assets/icons/ShapesDrawingTools/arrow.svg';
import { ReactComponent as DrawSvg } from '@assets/icons/ShapesDrawingTools/draw.svg';
import { ReactComponent as DndSvg } from '@assets/icons/ShapesDrawingTools/dnd.svg';
import { ReactComponent as CursorSvg } from '@assets/icons/ShapesDrawingTools/cursor.svg';
import { ReactComponent as CommentSvg } from '@assets/icons/ShapesDrawingTools/comment.svg';
import { ReactComponent as ShowCommentSvg } from '@assets/icons/ShapesDrawingTools/show_comment.svg';
import { ReactComponent as TextHighlightIcon } from '@assets/icons/ShapesDrawingTools/text_highlight.svg';

import './ShapesDrawingTools.less';

type ShapesDrawingToolsProps = {
  assetType?:
    | 'audio'
    | 'video'
    | 'document'
    | 'raster_image'
    | 'camera_raw_image'
    | 'vector_image'
    | 'presentation'
    | 'spreadsheet';
  player?: VideoJsPlayer | null;
};

function ShapesDrawingTools({
  assetType: assetTypeProp,
  player
}: ShapesDrawingToolsProps = {}) {
  const drawingToolsContainerRef = useRef<HTMLDivElement>(null);
  const isMouseDownRef = useRef<boolean>(false);
  isMouseDownRef.current = false;
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { isMdWidth } = useResponsive();
  const {
    shapesDrawingTools,
    isHideComments,
    selectedCommentId,
    selectedVersion,
    canvasMode,
    isCommentFieldFocused,
    setDrawToolsColor,
    setDrawToolsShape,
    selectComment,
    setDocumentHighlight,
    setCanvasMode,
    setHideComments
  } = useAssetViewerState();
  const assetTypeFromRedux = selectedVersion?.metadata.type;
  const assetType = assetTypeProp ?? assetTypeFromRedux;

  // const [openTextHighlightModal, setOpenTextHighlightModal] = useState(false);
  // const onboarding = useOnboardingProcess();
  const isShapeDrawing = shapesDrawingTools.shape !== 'cursor';

  const { history, mods, redoHistory, undoHistory } = useCanvasHistory({
    assetType
  });
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const [shape, setShape] = useState<string>('rectangle');
  const [isColorPickerVisible, setIsColorPickerVisible] =
    useState<boolean>(false);
  const [isShapePickerVisible, setIsShapePickerVisible] =
    useState<boolean>(false);

  const withKeysHistoryChange = useCallback(
    (e: KeyboardEvent) => {
      if (!isShapeDrawing) return;
      withKeysCanvasHistoryChange({
        e,
        undo: undoHistory,
        redo: redoHistory,
        historyLength: history.length,
        mods
      });
    },
    [history.length, mods, redoHistory, undoHistory, isShapeDrawing]
  );

  const onShapeClick = useCallback(
    (shape: string) => {
      if (shape === 'cursor') {
        if (selectedCommentId === 'new') {
          selectComment(null);
          setDocumentHighlight(null);
          setCanvasMode('view');
        }
        setDrawToolsShape('cursor');
        return;
      }
      if (!user) {
        dispatch(
          toggleSingInExternalReviewerModal({
            visible: true
          })
        );
        return;
      }
      if (canvasMode === 'view') {
        if (selectedCommentId !== 'reply') {
          selectComment('new');
          setDocumentHighlight(null);
        }
        setCanvasMode('edit');
      }
      if (player) player.pause();
      setDrawToolsShape(shape);
    },
    [
      canvasMode,
      dispatch,
      player,
      selectedCommentId,
      user,
      setDrawToolsShape,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    ]
  );

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const modalElement = document.querySelector('.ant-modal-root');
      const fieldElement = document.querySelector(
        '.filter-sort-panel-search-input'
      );
      const transcriptSearchInput = document.getElementById(
        'TRANSCRIPT_SEARCH_INPUT'
      );
      withKeysHistoryChange(e);

      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (isTypingTarget) return;

      if (
        modalElement ||
        fieldElement ||
        transcriptSearchInput ||
        isCommentFieldFocused
      )
        return;
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === 'h') {
        e.preventDefault();
        setHideComments(!isHideComments);
        return;
      }
      if (key === 'v') {
        onShapeClick('cursor');
        return;
      }
      if (key === 'd') {
        onShapeClick('brush');
        return;
      }
      if (key === 'a') {
        onShapeClick('arrow');
        return;
      }
      if (key === 'h' && isDocument) {
        onShapeClick('documentHighlight');
        return;
      }
      if (key === 'c' && assetType !== 'audio' && assetType !== 'video') {
        onShapeClick('pin');
      }
    };
    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [
    withKeysHistoryChange,
    onShapeClick,
    isCommentFieldFocused,
    isDocument,
    assetType,
    isHideComments,
    setHideComments
  ]);

  useEffect(() => {
    const callback = (e: any) => {
      if (!isMouseDownRef.current || !drawingToolsContainerRef.current) return;
      const skip = e.pageX === 0 && e.pageY === 0;
      if (!skip) {
        drawingToolsContainerRef.current.style.transform = `translate(${e.pageX}px, ${e.pageY}px)`;
      }
    };
    document.addEventListener('mousemove', callback);
    return () => {
      document.removeEventListener('mousemove', callback);
    };
  }, []);

  const undoRedoTooltips = useMemo(() => {
    return withKeysCanvasHistoryCopy();
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains('dragger')) return;
      if (!drawingToolsContainerRef.current) return;
      isMouseDownRef.current = true;
      const rect = drawingToolsContainerRef.current.getBoundingClientRect();
      drawingToolsContainerRef.current.style.position = 'fixed';
      drawingToolsContainerRef.current.style.top = `${rect.y - e.pageY}px`;
      drawingToolsContainerRef.current.style.right = 'unset';
      drawingToolsContainerRef.current.style.left = `${rect.x - e.pageX}px`;
      drawingToolsContainerRef.current.style.transform = `translate(${e.pageX}px, ${e.pageY}px)`;
      drawingToolsContainerRef.current.style.zIndex = '1000';
    },
    []
  );

  const onMouseUp = useCallback(() => {
    isMouseDownRef.current = false;
  }, []);

  return (
    <div
      ref={drawingToolsContainerRef}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      className={classNames(
        isShapeDrawing && 'shapes_drawing_tools_container_open',
        'shapes_drawing_tools_container',
        'tour-review-asset-tool-bar'
      )}
    >
      <div className="wrapper">
        <Tooltip
          title={
            <div
              onClick={() => {
                onShapeClick('pin');
              }}
              className="media-viewer-tooltip-inner"
              style={{ padding: 8 }}
            >
              Select
              <span className="media-viewer-tooltip-label">V</span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            className={classNames(
              shapesDrawingTools?.shape === 'cursor' && 'selected',
              'anticon anticon-font-size shapes_drawing_tools-item'
            )}
            onClick={() => {
              onShapeClick('cursor');
            }}
          >
            <CursorSvg />
          </span>
        </Tooltip>

        {assetType !== 'audio' && assetType !== 'video' && (
          <Tooltip
            title={
              <div
                onClick={() => {
                  onShapeClick('pin');
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Comment
                <span className="media-viewer-tooltip-label">C</span>
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
          >
            <span
              className={classNames(
                shapesDrawingTools?.shape === 'pin' && 'selected',
                'anticon anticon-font-size shapes_drawing_tools-item'
              )}
              onClick={() => {
                onShapeClick('pin');
              }}
            >
              <CommentSvg />
            </span>
          </Tooltip>
        )}
        <span className="divider b-full" />
        <Tooltip
          title={
            <div
              onClick={() => {
                setIsColorPickerVisible(true);
              }}
              className="media-viewer-tooltip-inner"
              style={{ padding: 8 }}
            >
              Switch color
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <Tooltip
            placement={isMdWidth ? 'top' : 'right'}
            overlayClassName="color_picker_overlay"
            trigger={['click']}
            open={isColorPickerVisible}
            onOpenChange={(v) => setIsColorPickerVisible(v)}
            title={
              <div className="color_picker_overlay_container">
                {colorPickerColorScheme.map((el) => (
                  <div
                    key={el}
                    className="color_picker_overlay_container__item"
                    style={{ background: el }}
                    onClick={() => {
                      setIsColorPickerVisible(false);
                      setDrawToolsColor(el);
                      if (shapesDrawingTools.shape === 'pin')
                        onShapeClick('brush');
                    }}
                  />
                ))}
              </div>
            }
          >
            <div
              className="color_picker shapes_drawing_tools-item"
              style={{ background: shapesDrawingTools?.color }}
            />
          </Tooltip>
        </Tooltip>
        <span className="divider" />
        {isDocument && (
          <Tooltip
            title={
              <div
                onClick={() => {
                  onShapeClick('documentHighlight');
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Text highlight
                <span className="media-viewer-tooltip-label">H</span>
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
          >
            <span
              className={classNames(
                shapesDrawingTools?.shape === 'documentHighlight' && 'selected',
                'anticon anticon-font-size shapes_drawing_tools-item tour-text-highlight-bar'
              )}
              onClick={() => onShapeClick('documentHighlight')}
            >
              <TextHighlightIcon />
            </span>
          </Tooltip>
        )}
        {/* <Tooltip
              title={
                <div
                  onClick={() => {
                    onShapeClick('text');
                  }}
                  className="media-viewer-tooltip-inner"
                >
                  Text
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="right"
              overlayClassName="toggle_favorite_overlay"
            >
              <FontSizeOutlined
                className={classNames(
                  shapesDrawingTools?.shape === 'text' && 'selected',
                  'shapes_drawing_tools-item'
                )}
                onClick={() => {
                  onShapeClick('text');
                }}
              />
            </Tooltip> */}
        <Tooltip
          title={
            <div
              onClick={() => {
                onShapeClick('arrow');
              }}
              className="media-viewer-tooltip-inner"
              style={{ padding: 8 }}
            >
              Arrow
              <span className="media-viewer-tooltip-label">A</span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            className={classNames(
              shapesDrawingTools?.shape === 'arrow' && 'selected',
              'anticon anticon-font-size shapes_drawing_tools-item'
            )}
            onClick={() => {
              onShapeClick('arrow');
            }}
          >
            <ArrowSvg />
          </span>
        </Tooltip>
        <Tooltip
          title={
            <div
              onClick={() => {
                setIsShapePickerVisible(true);
                onShapeClick(shape);
              }}
              className="media-viewer-tooltip-inner"
              style={{ padding: 8 }}
            >
              Shape tools
            </div>
          }
          overlayInnerStyle={{
            padding: 0,
            display: isShapePickerVisible ? 'none' : 'block'
          }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <div
            onClick={() => {
              onShapeClick(shape);
              if (shapesDrawingTools?.shape === shape) {
                setIsShapePickerVisible(true);
              }
            }}
            className={classNames(
              'shape_picker',
              'shapes_drawing_tools-item',
              shapesDrawingTools?.shape === shape && 'selected'
            )}
          >
            <div className={classNames(`shape_picker__${shape}`)} />
            <Tooltip
              placement={isMdWidth ? 'top' : 'right'}
              overlayClassName="shape_picker_overlay"
              trigger={['click']}
              open={isShapePickerVisible}
              onOpenChange={(v) => setIsShapePickerVisible(v)}
              title={
                <div className="shape_picker_overlay_container">
                  {shapesScheme.map((el) => (
                    <Tooltip
                      title={el}
                      key={el}
                      placement="right"
                      overlayInnerStyle={{
                        padding: '9px 8px',
                        textTransform: 'capitalize'
                      }}
                      overlayClassName="toggle_favorite_overlay"
                    >
                      <div
                        key={el}
                        className={`shape_picker_overlay_container__item ${
                          shape === el &&
                          'shape_picker_overlay_container__selected_item'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsShapePickerVisible(false);
                          onShapeClick(el);
                          setShape(el);
                        }}
                      >
                        <div className={`shape shape_${el}`} />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              }
            />
          </div>
        </Tooltip>
        <Tooltip
          title={
            <div
              onClick={() => {
                onShapeClick('brush');
              }}
              className="media-viewer-tooltip-inner"
              style={{ padding: 8 }}
            >
              Draw
              <span className="media-viewer-tooltip-label">D</span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            className={classNames(
              'anticon anticon-font-size',
              'shape_picker',
              'shapes_drawing_tools-item',
              shapesDrawingTools?.shape === 'brush' && 'selected'
            )}
            onClick={() => {
              onShapeClick('brush');
            }}
          >
            <DrawSvg />
          </span>
        </Tooltip>
        <span className="divider b-full" />
        <Tooltip
          title={
            <div className="media-viewer-tooltip-inner" style={{ padding: 8 }}>
              {isHideComments ? 'Show' : 'Hide'} comments
              <span className="media-viewer-tooltip-label">
                {undoRedoTooltips.toggleShowComments}
              </span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            className={classNames(
              isHideComments && 'selected',
              'anticon anticon-font-size shapes_drawing_tools-item'
            )}
            onClick={() => {
              setHideComments(!isHideComments);
            }}
          >
            <ShowCommentSvg />
          </span>
        </Tooltip>
      </div>
      <div className="wrapper">
        <Tooltip
          title={
            <div
              style={{ padding: 8 }}
              onClick={undoHistory}
              className="media-viewer-tooltip-inner"
            >
              Undo
              <span className="media-viewer-tooltip-label">
                {undoRedoTooltips.undo}
              </span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            className={classNames(
              'anticon anticon-font-size',
              mods < history.length && 'arrow_active',
              'arrow',
              'shapes_drawing_tools-item'
            )}
            onClick={undoHistory}
          >
            <BackSvg />
          </span>
        </Tooltip>
        <Tooltip
          title={
            <div
              style={{ padding: 8 }}
              onClick={redoHistory}
              className="media-viewer-tooltip-inner"
            >
              Redo
              <span className="media-viewer-tooltip-label">
                {undoRedoTooltips.redo}
              </span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            className={classNames(
              'anticon anticon-font-size',
              mods > 0 && 'arrow_active',
              'arrow',
              'shapes_drawing_tools-item'
            )}
            onClick={redoHistory}
          >
            <ForwardSvg />
          </span>
        </Tooltip>
      </div>
      <LgWidth>
        <Tooltip
          title="Move toolbar"
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <div className="dragger">
            <DndSvg />
          </div>
        </Tooltip>
      </LgWidth>
      {/* Can delete */}
      {/* {isDocument && (
        <TextHighlightModal
          onClose={() => {
            onboarding.onCompleteStep({
              type: 'text-highlight',
              hideCongrats: true
            });
            setOpenTextHighlightModal(false);
          }}
          open={openTextHighlightModal}
        />
      )} */}
    </div>
  );
}
export default ShapesDrawingTools;
